import { useCallback, useEffect, useState } from 'react'
import { isArticleGradient } from '@/features/instructions/lib/articleStyle'

const STORAGE_KEY = 'instruction-custom-gradients'

const load = (): string[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed)
      ? parsed.filter((s): s is string => typeof s === 'string' && isArticleGradient(s))
      : []
  } catch {
    return []
  }
}

export const useCustomGradients = () => {
  const [gradients, setGradients] = useState<string[]>(() => load())

  const persist = useCallback((next: string[]) => {
    setGradients(next)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  }, [])

  const add = useCallback(
    (g: string) => {
      if (!isArticleGradient(g) || gradients.includes(g)) return
      persist([...gradients, g])
    },
    [gradients, persist],
  )

  const remove = useCallback(
    (g: string) => {
      persist(gradients.filter((x) => x !== g))
    },
    [gradients, persist],
  )

  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setGradients(load())
    }
    window.addEventListener('storage', handler)
    return () => window.removeEventListener('storage', handler)
  }, [])

  return { gradients, add, remove }
}
